import { Menu, Transition } from "@headlessui/react";
import {
  ChevronDownIcon,
  InformationCircleIcon,
} from "@heroicons/react/outline";
import StepButtons from "components/lib/Global/StepButtons";
import Button from "components/lib/Shared/Button";
import Tabs from "components/lib/Shared/Tabs";
import { Constants } from "config/constants";
import { Roles_Permissions_Constants } from "config/roles-permissions";
import { usePopper } from "hooks/usePopper";
import { useToast } from "hooks/useToast";
import Cookies from "js-cookie";
import _pick from "lodash/pick";
import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  submitApplicationToBRS,
  updateCompanyAsync,
} from "state/slices/company";
import { getReviewers } from "state/slices/tables/reviewersTable";
import { updateLinkedCompanies } from "state/slices/user";
import { classNames, getFullName } from "utils";
import { getCompanyNameWithSuffix } from "utils/company";
import { setCookie } from "utils/cookie";
import AddressParticulars from "./AddressParticulars";
import ApplicantDetails from "./ApplicantDetails";
import AssignedReviewer from "./AssignedReviewer";
import AuditTrail from "./AuditTrail";
import BeneficialOwnership from "./BeneficialOwnership";
import BusinessDocuments from "./BusinessDocuments";
import CompanyFormation from "./CompanyFormation";
import StakeholderDetails from "./CompanyOfficials/StakeholderDetails";
import ContactPerson from "./ContactPerson";
import FileUploads from "./FileUploads";
import ShareInformation from "./ShareInformation";
import SignatureSelection from "./SignatureSelection";
import Status from "./Status";
import TypeOfChange from "./TypeOfChange";
import Summary from "./Summary";

export default function TransferYourBusinessReviewTab({
  handleBackBtn,
  ...restProps
}) {
  // =====================
  // Variable Declarations
  // =====================
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [disableSubmitBtn, setDisableSubmitBtn] = useState(true);
  const [isSubmitting, setSubmitting] = useState(false);
  const [isDeferring, setIsDeferring] = useState(false);
  const [isVetting, setIsVetting] = useState(false);
  const [agreeToSubmit, setAgreeToSubmit] = useState(true);
  const [agreeToTerms, setAgreeToTerms] = useState(true);
  const [agreeToSubmitToBrs, setAgreeToSubmitToBrs] = useState(true);
  const currentUser = useSelector((state) => state.userSlice);
  const { names } = currentUser || {};
  const currentUserFullName = getFullName(names);
  const history = useHistory();
  const dispatch = useDispatch();
  const { toast } = useToast(4000);
  const { company = {} } = useSelector((state) => state.companySlice);
  const { comments } = useSelector((state) => state.commentsSlice);
  const companyStatus = company?.status ?? "";
  const companyId = company?._id ?? "";
  const isViewMode =
    history.location.pathname.split("/").pop() === "view" ||
    history.location.pathname.split("/").pop() === "transfer-view" ||
    [("Pending Incorporation", "Vetting", "Active")].includes(companyStatus) ||
    restProps?.isViewMode;
  const {
    manageCompanyTransactions,
    dashboardPermissions,
    companyOfficials,
    dashboardRoles,
    designation,
  } = useSelector((state) => state.companyDashboardSlice);
  const isReviewer = checkIsReviewer({ company, currentUser, dashboardRoles });
  const isApprover = loggedInUserHasApproverRole(dashboardRoles);
  const isReviewerApprover = isReviewer && isApprover;
  const isOrgAdmin = Cookies.get("role") === Constants.COOKIE_ROLES.ORG_ADMIN;
  const isSuperAdmin =
    Cookies.get("role") === Constants.COOKIE_ROLES.SUPER_ADMIN;
  const [initialTabs, setInitialTabs] = useState([]);
  const [tabs, setTabs] = useState(initialTabs);
  const [selectedTab, setSelectedTab] = useState();
  const [esignatureIsSelected, setEsignatureIsSelected] = useState(false);
  const [trigger, container] = usePopper({
    placement: "bottom-end",
    strategy: "fixed",
    modifiers: [{ name: "offset", options: { offset: [0, 10] } }],
  });
  const unresolvedComments = comments.filter((comment) => !comment.isResolved);
  const submitToLabel =
    unresolvedComments.length !== 0
      ? "applicant"
      : isOrgAdmin
      ? "incorporate"
      : isReviewer
      ? "admin"
      : "";
  let companyName = getCompanyNameWithSuffix(company);

  const isLastStep = tabs.length - 1 === selectedTabIndex;

  let nextBtnLabel = isLastStep
    ? isViewMode
      ? "Back to Transactions Page"
      : `Submit to ${submitToLabel}`
    : isViewMode
    ? "Next Step"
    : "Confirm details";
  if (isOrgAdmin) {
    // For Vetting Status
    if (companyStatus === "Vetting" && isLastStep) {
      nextBtnLabel = "Incorporate";
    }

    // For Pending Incorporation Status
    if (companyStatus === "Admin Review" && isLastStep) {
      nextBtnLabel = "Incorporate";
    }
  }

  /** Check if the logged in user is Reviewer or Approver at any stage */
  const { dashboardRoles: adminRoles, dashboardPermissions: adminPermissions } =
    useSelector((state) => state.activeDashboardSlice);

  // Org Admin Roles
  const isOrgAdminReviewer = adminRoles.some(
    (role) => role.name === Roles_Permissions_Constants.ORG_ADMIN_ROLES.REVIEWER
  );
  const isOrgAdminApprover = adminRoles.some(
    (role) =>
      role.name === Roles_Permissions_Constants.ORG_ADMIN_ROLES.APPROVER ||
      role.name === Roles_Permissions_Constants.DASHBOARD_ROLES.REGISTRAR
  );
  const isOrgAdminUploader = adminRoles.some(
    (role) => role.name === Roles_Permissions_Constants.ORG_ADMIN_ROLES.UPLOADER
  );

  // Super Admin Roles
  const isSuperAdminReviewer = adminRoles.some(
    (role) =>
      role.name === Roles_Permissions_Constants.SUPER_ADMIN_ROLES.REVIEWER
  );
  const isSuperAdminApprover = adminRoles.some(
    (role) =>
      role.name === Roles_Permissions_Constants.SUPER_ADMIN_ROLES.APPROVER
  );
  const isSuperAdminUploader = adminRoles.some(
    (role) =>
      role.name === Roles_Permissions_Constants.SUPER_ADMIN_ROLES.UPLOADER
  );

  //Brs Ops Roles
  const isBrsOpsReviewer = adminRoles.some(
    (role) => role.name === Roles_Permissions_Constants.BRS_OPS_ROLES.REVIEWER
  );
  const isBrsOpsApprover = adminRoles.some(
    (role) => role.name === Roles_Permissions_Constants.BRS_OPS_ROLES.APPROVER
  );
  const isBrsOpsUploader = adminRoles.some(
    (role) => role.name === Roles_Permissions_Constants.BRS_OPS_ROLES.UPLOADER
  );

  // =====================
  // Function Declarations
  // =====================

  // Handles deferring the application
  const handleDeferApplication = async () => {
    try {
      setIsDeferring(true);
      let status = companyStatus;
      if (
        ["Directors Review", "Directors Corrections"].includes(companyStatus)
      ) {
        status = "Directors Deferred";
      } else if (
        ["Admin Review", "Admin Review II", "Admin Corrections"].includes(
          companyStatus
        )
      ) {
        status = "Admin Deferred";
      }
      await dispatch(
        updateCompanyAsync({ id: company?._id, data: { status } })
      );
      toast(
        "success",
        "The application has been successfully marked as deferred."
      );
      history.push("/");
    } catch (error) {
      toast(
        "error",
        error?.message ?? "Something went wrong please try again."
      );
    } finally {
      setIsDeferring(false);
    }
  };

  // Go to previous step/tab
  const goToPrevStep = () => {
    if (selectedTabIndex === 0 && handleBackBtn) handleBackBtn();
    else setSelectedTabIndex(selectedTabIndex - 1);
  };

  // Go to next step/tab, with business logic for submission
  const goToNextStep = async () => {
    const lastTabIndex = tabs.length - 1;
    if (selectedTabIndex < lastTabIndex) {
      setSelectedTabIndex(selectedTabIndex + 1);
      return;
    }

    if (companyStatus === "Active") {
      handleBackBtn();
      return;
    }
    if (
      isViewMode &&
      companyStatus !== "Pending Incorporation" &&
      companyStatus !== "Vetting" &&
      companyStatus !== "Incorporation Deferred"
    ) {
      setCookie("role", Constants.COOKIE_ROLES.APPLICANT);
      let initiator = {};
      let link = "";
      if (company?.transferYourBusinessInitiator?.company) {
        initiator = company?.transferYourBusinessInitiator?.company;
        link = `/companies/${initiator._id}/dashboard?transactions`;
      }
      if (company?.transferYourBusinessInitiator?.corporate) {
        initiator = company?.transferYourBusinessInitiator?.corporate;
        link = `/corporates/${initiator._id}/dashboard?transactions`;
      }
      if (link) {
        history.push(link);
      }

      return;
    }

    if (isOrgAdmin && unresolvedComments.length === 0) {
      handleIncorporation();
    } else handleSubmitApplication();
  };

  // Handles application submission (to admin, BRS, or for corrections)
  const handleSubmitApplication = async () => {
    let status = "";
    let toastMessage = "";
    if (unresolvedComments.length > 0) {
      toastMessage =
        "The application has been successfully sent to the applicant for corrections.";
      if (isReviewer) status = "Directors Corrections";
      if (isOrgAdmin) status = "Admin Corrections";
    } else {
      if (isReviewer) {
        status = "Admin Review";
        toastMessage =
          "The application has been successfully sent to the admins.";
      }
    }
    try {
      setSubmitting(true);
      const submitApplicationPromise = dispatch(
        updateCompanyAsync({
          id: companyId,
          data: {
            status,
          },
        })
      );
      await Promise.all([submitApplicationPromise]);
      toast("success", toastMessage);
    } catch (error) {
      // Error handled by toast in catch
    } finally {
      setSubmitting(false);
    }
    history.push("/");
  };

  // Handles incorporation
  const handleIncorporation = async () => {
    setSubmitting(true);
    try {
      const updateCompanyData = dispatch(
        updateCompanyAsync({
          id: companyId,
          data: {
            status: isOrgAdminApprover
              ? Constants.COMPANY_REVIEW_STATUSES.ACTIVE
              : Constants.COMPANY_REVIEW_STATUSES.ADMIN_APPROVAL,
            fileUploads: company?.fileUploads,
            ..._pick(company, [
              "registrationName",
              "registrationDate",
              "kraPin",
              "NSSFNo",
              "NHIFNo",
            ]),
          },
        })
      );
      toast("success", "The application has been successfully incorporated.");
    } catch (error) {
      // Error handled by toast in catch
    } finally {
      setSubmitting(false);
    }
    history.push("/applications/all-applications");
  };

  // =====================
  // Effects
  // =====================
  useEffect(() => {
    const companyFormationCommentsCounts =
      comments.filter((comment) => {
        if (
          ["articlesOfAssociation", "businessApplicationDetails"].includes(
            comment.formName
          ) ||
          comment.formName.includes(`preferredCompanyNameCard_`)
        )
          return true;
      })?.length ?? 0;

    const addressParticularsCommentsCount =
      comments.filter((comment) => comment.formName === "addressParticulars")
        ?.length ?? 0;
    const shareInformationCommentsCount =
      comments.filter((comment) => comment.formName === "shareInformation")
        ?.length ?? 0;
    const companyOfficialsCommentCount =
      comments.filter((comment) => comment.tabName === "stakeholderTab")
        ?.length ?? 0;
    const beneficialOwnersCommentCount =
      comments.filter((comment) => comment.tabName === "beneficialOwnership")
        ?.length ?? 0;
    const tempTabs = [
      {
        id: "applicantDetails",
        label: "Applicant Details",
      },
      {
        id: "typeOfChange",
        label: "Type of Change",
      },
      {
        id: "summary",
        label: "Summary",
        commentsCount: companyFormationCommentsCounts,
      },
      {
        id: "addressParticulars",
        label: "Address particulars",
        commentsCount: addressParticularsCommentsCount,
      },
      {
        id: "shareInformation",
        label: "Share information",
        commentsCount: shareInformationCommentsCount,
      },
      {
        id: "stakeholderDetails",
        label: "Stakeholder details",
        commentsCount: companyOfficialsCommentCount,
      },
      {
        id: "beneficialOwnership",
        label: "Beneficial ownership",
        commentsCount: beneficialOwnersCommentCount,
      },
      { id: "contactPerson", label: "Contact Person" },
      { id: "status", label: "Status" },
    ];
    setInitialTabs(tempTabs);
    setTabs(tempTabs);
    setSelectedTab(tempTabs[0]);
  }, [comments.length]);

  useEffect(() => {
    if (companyId) {
      dispatch(getReviewers({ companyId }));
    }
  }, [companyId]);

  useEffect(() => {
    const tempSelectedTab = tabs[selectedTabIndex];
    if (tempSelectedTab) setSelectedTab(tempSelectedTab);
  }, [tabs, selectedTabIndex]);

  useEffect(() => {
    if (companyId) {
      const selectedLinkedCompany = currentUser?.linkedCompanies.find(
        (linkedCompany) => linkedCompany.company?._id === companyId
      );
      setAgreeToSubmit(true);
      setAgreeToTerms(true);
    }
  }, [currentUser.linkedCompanies, companyId]);

  useEffect(() => {
    setAgreeToSubmitToBrs(true);
  }, [company.agreeToSubmit]);

  // For BRS Review Status
  let submitBtnDropdownOptions = [];
  submitBtnDropdownOptions.push({
    name: "Mark as Deferred",
    handleOnClick: handleDeferApplication,
    disabled: company?.status?.includes("Deferred"),
  });

  const SubmitBtnDropdown = (
    <Menu as="div" className="relative -ml-px block">
      <Menu.Button
        ref={trigger}
        disabled={isSubmitting || isDeferring}
        className="relative inline-flex border-l border-white items-center rounded-r-md bg-green-focused px-2 py-2 text-white hover:bg-green-700 focus:z-10 disabled:cursor-not-allowed disabled:bg-green-200"
      >
        <span className="sr-only">Open options</span>
        <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
      </Menu.Button>
      <div ref={container} className="z-50">
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 z-10 -mr-1 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            <div className="py-1">
              {submitBtnDropdownOptions.map((item) => (
                <Menu.Item key={item.name}>
                  {({ active }) => (
                    <Button
                      onClick={item.handleOnClick}
                      variant="text"
                      disabled={item.disabled}
                      className={classNames(
                        active ? "bg-gray-100 text-gray-900" : "text-gray-700",
                        "block w-full px-4 py-2 text-sm"
                      )}
                    >
                      {item.name}
                    </Button>
                  )}
                </Menu.Item>
              ))}
            </div>
          </Menu.Items>
        </Transition>
      </div>
    </Menu>
  );

  // =====================
  // Render
  // =====================
  if (!selectedTab || !companyId) return "loading...";

  return (
    <>
      {companyStatus !== "Active" &&
        !company?.transferYourBusinessInitiator && (
          <>
            <h2 className="mb-4 text-primary text-title6 font-semibold">
              {company?.companyName1 ? companyName : "Application reviews"}
            </h2>
            <div className="rounded-md bg-sky-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <InformationCircleIcon
                    className="h-5 w-5 text-sky-400"
                    aria-hidden="true"
                  />
                </div>
                <div className="ml-3 flex-1 md:flex md:justify-between whitespace-normal">
                  <p className="text-sm text-sky-700">
                    {selectedTab.id === "confirmation"
                      ? "Please confirm if the forms uploaded are correct."
                      : "Please confirm if the data submitted is correct. If not, please leave comments which will be amended by the applicant."}
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      <div className="overflow-x-auto">
        <Tabs
          tabs={tabs}
          selectedTabIndex={selectedTabIndex}
          setSelectedTabIndex={setSelectedTabIndex}
          showTooltip
        />
      </div>
      {selectedTab.id === "applicantDetails" && (
        <ApplicantDetails isViewMode={isViewMode} />
      )}
      {selectedTab.id === "typeOfChange" && (
        <TypeOfChange isViewMode={isViewMode} />
      )}
      {selectedTab.id === "summary" && (
        <Summary
          isViewMode={isViewMode}
          goToNextParentTab={goToNextStep}
          goToPrevParentTab={goToPrevStep}
        />
      )}
      {selectedTab.id === "addressParticulars" && (
        <AddressParticulars isViewMode={isViewMode} />
      )}
      {selectedTab.id === "shareInformation" && (
        <ShareInformation disabledRightsEditor isViewMode={isViewMode} />
      )}
      {selectedTab.id === "stakeholderDetails" && (
        <StakeholderDetails isViewMode={isViewMode} />
      )}
      {selectedTab.id === "beneficialOwnership" && (
        <BeneficialOwnership
          goToNextParentTab={goToNextStep}
          goToPrevParentTab={goToPrevStep}
          hideReviewWorkflow={isViewMode}
          isViewMode={isViewMode}
        />
      )}
      {selectedTab.id === "assignedReviewers" && <AssignedReviewer />}
      {selectedTab.id === "fileUploads" && <FileUploads />}
      {selectedTab.id === "status" && (
        <Status
          submitToLabel={submitToLabel}
          setDisableSubmitBtn={setDisableSubmitBtn}
          agreeToSubmit={agreeToSubmit}
          agreeToTerms={agreeToTerms}
          setAgreeToSubmit={setAgreeToSubmit}
          setAgreeToTerms={setAgreeToTerms}
        />
      )}
      {selectedTab.id === "manualBridge" && (
        <div className="px-4 mt-4 space-y-6 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6 whitespace-normal">
          <p>
            Information is populated to the E-Citizen platform manually to
            generate the required documents for Electronic signing.
          </p>
          <p>
            Once all the incorporation forms have been generated, click "Confirm
            details" below to uploaded them in the next step.
          </p>
        </div>
      )}
      {selectedTab.id === "businessDocuments" && (
        <BusinessDocuments
          handleBackBtn={goToPrevStep}
          handleNextBtn={goToNextStep}
          setEsignatureIsSelected={setEsignatureIsSelected}
          esignatureIsSelected={esignatureIsSelected}
        />
      )}
      {selectedTab.id === "contactPerson" && (
        <ContactPerson
          handleBackBtn={goToPrevStep}
          handleNextBtn={goToNextStep}
        />
      )}
      {selectedTab.id === "auditTrail" && <AuditTrail />}
      {selectedTab.id === "signatureSelection" && (
        <SignatureSelection
          esignatureIsSelected={esignatureIsSelected}
          agreeToSubmitToBrs={agreeToSubmitToBrs}
          setAgreeToSubmitToBrs={setAgreeToSubmitToBrs}
          handleBackBtn={handleBackBtn}
          goToNextStep={goToNextStep}
        />
      )}
      {![
        "businessDocuments",
        "summary",
        "contactPerson",
        "beneficialOwnership",
      ].includes(selectedTab.id) && (
        <StepButtons
          isFirstStep
          goToPrevStep={goToPrevStep}
          goToNextStep={goToNextStep}
          prevBtnLabel={"Previous Step"}
          nextBtnLink={null}
          nextBtnLabel={nextBtnLabel}
          loadingText={isDeferring ? "Deferring..." : "Submitting..."}
          isNextBtnLoading={isSubmitting || isDeferring || isVetting}
          isNextBtnDisabled={
            (!agreeToSubmitToBrs && selectedTab.id === "confirmation") ||
            (esignatureIsSelected && selectedTab.id === "signatureSelection")
          }
          isLastStep={isLastStep}
          submitBtnDropdown={
            submitBtnDropdownOptions.length > 0 ? SubmitBtnDropdown : null
          }
        />
      )}
    </>
  );
}

function checkIsReviewer({ company, currentUser, dashboardRoles }) {
  const { createdBy } = company;
  return (
    companyWasCreatedByLoggedInUser(createdBy?.email, currentUser?.email) ||
    loggedInUserHasReviewerRole(dashboardRoles)
  );
}

function companyWasCreatedByLoggedInUser(createdByEmail, currentUserEmail) {
  return createdByEmail === currentUserEmail;
}

function loggedInUserHasReviewerRole(dashboardRoles) {
  return dashboardRoles.some(
    (role) => role.name === Roles_Permissions_Constants.DASHBOARD_ROLES.REVIEWER
  );
}

function loggedInUserHasApproverRole(dashboardRoles) {
  return dashboardRoles.some(
    (role) => role.name === Roles_Permissions_Constants.DASHBOARD_ROLES.APPROVER
  );
}
